import React, { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { Card, Switch, Text, Title } from '@tremor/react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';
import {
  useWorkspaceSettings,
  workspaceSettingsQueryKey,
} from './useWorkspaceSettings';

const API_HOST = process.env.REACT_APP_API_HOST;

const privacySettings = [
  {
    id: 'calendarView',
    name: 'Calendar View',
    description: 'Calendar View shows the absences of a user over a full year. It\'s the same as "Calendar" from the main menu. Hiding this means that screen is only visible to a user, their manager/admin users.',
  },
  {
    id: 'otherDepartments',
    name: 'Other Departments',
    description: 'When visible, users can view the calendars of any department. Hiding departments restricts users to only seeing the calendar of their own department. Note*: This doesn\'t affect admin users.',
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Send me email notifications about adding me to teams.',
  },
];

export const PrivacyForm = () => {
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const mapPrivacySettings = (privacy: Record<string, boolean>) => ({
    calendarView:
      privacy?.calendarView ?? privacy?.isCalendarView ?? false,
    otherDepartments:
      privacy?.otherDepartments ?? privacy?.isOtherDepartments ?? false,
    notifications:
      privacy?.notifications ?? privacy?.isNotificationsEnabled ?? false,
  });

  const formik = useFormik({
    initialValues: {
      calendarView: false,
      otherDepartments: false,
      notifications: false,
    },
    validationSchema: yup.object({
      calendarView: yup.boolean(),
      otherDepartments: yup.boolean(),
      notifications: yup.boolean(),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const response = await axios.patch(`${API_HOST}/v1/workspace`, values);
        if (response.status === 200) {
          toast.success('Privacy settings updated successfully!');
        } else {
          toast.error(`Error updating privacy settings: ${response.statusText}`);
        }
      } catch (error) {
        console.error("Error saving privacy settings:", error);
        let message = 'An unexpected error occurred while saving privacy settings.';
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.data) {
            const errorData = axiosError.response.data as any;
            if (errorData.message) {
              message = Array.isArray(errorData.message) ? errorData.message.join('; ') : String(errorData.message);
            } else {
              message = axiosError.message;
            }
          } else {
            message = axiosError.message;
          }
        }
        toast.error(message);
      }
    },
  });
  const { setValues } = formik;

  const { data, isLoading, error } = useWorkspaceSettings();

  useEffect(() => {
    if (data?.privacy) {
      setValues(mapPrivacySettings(data.privacy));
    }
  }, [data, setValues]);

  useEffect(() => {
    if (!error) return;

    console.error('Error fetching workspace settings:', error);
    let errorMessage = 'Failed to load workspace settings.';
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message || errorMessage;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
  }, [error]);

  const handleToggle = async (settingId: string, checked: boolean) => {
    const originalState = formik.values[settingId as keyof typeof formik.values];
    formik.setFieldValue(settingId, checked);
    setIsUpdating((prev) => ({ ...prev, [settingId]: true }));

    try {
      const response = await axios.patch(`${API_HOST}/v1/workspace`, { [settingId]: checked });
      const result = response.data;

      if (result.status !== 'UPDATE_WORKSPACE_SETTINGS_SUCCESS') {
        console.error("API did not return success status:", result);
        throw new Error('API indicated update failure.');
      }

      await queryClient.invalidateQueries(workspaceSettingsQueryKey);
      toast.success(`Setting '${privacySettings.find(s => s.id === settingId)?.name}' ${checked ? 'enabled' : 'disabled'}.`);

    } catch (error) {
      console.error(`Error updating setting ${settingId}:`, error);
      let errorMessage = 'Unknown error occurred';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message || 'Failed to update privacy setting';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast.error(`Failed to update setting: ${errorMessage}`);
      formik.setFieldValue(settingId, originalState);
    } finally {
      setIsUpdating((prev) => ({ ...prev, [settingId]: false }));
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <Title>Privacy Settings</Title>
        <Text>Loading privacy settings...</Text>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <Title>Privacy Settings</Title>
      <Text>Configure visibility and notification settings for your workspace.</Text>

      <div className="mt-6 space-y-6">
        {privacySettings.map((setting) => (
          <div
            key={setting.id}
            className="flex items-center justify-between space-x-4 rounded-tremor-default border border-tremor-border p-4 dark:border-dark-tremor-border"
          >
            <div>
              <h4 className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
                {setting.name}
              </h4>
              <p className="mt-1 text-tremor-default leading-6 text-tremor-content dark:text-dark-tremor-content">
                {setting.description}
              </p>
            </div>
            <Switch
              id={setting.id}
              name={setting.name}
              checked={formik.values[setting.id as keyof typeof formik.values] || false}
              onChange={(checked) => handleToggle(setting.id, checked)}
              color="rose"
              disabled={isUpdating[setting.id]}
            />
          </div>
        ))}
      </div>
    </Card>
  );
};
